import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { resolveImageUrl } from "@/lib/image";

const CATEGORY_ACCENTS = {
  Cyberpunk: "from-[#00E5FF]/40 to-transparent",
  Corporate: "from-white/25 to-transparent",
  Editorial: "from-white/25 to-transparent",
  Anime: "from-[#E0007A]/40 to-transparent",
  Vintage: "from-amber-200/30 to-transparent",
  Minimalist: "from-white/25 to-transparent",
  Street: "from-[#00E5FF]/40 to-transparent",
  Fantasy: "from-violet-300/40 to-transparent",
  Cottagecore: "from-amber-100/30 to-transparent",
};

const LABEL_STYLES = {
  Cyberpunk: "text-[#00E5FF] bg-[#00E5FF]/10 border-[#00E5FF]/25",
  Corporate: "text-white/85 bg-white/5 border-white/15",
  Editorial: "text-white/85 bg-white/5 border-white/15",
  Anime: "text-[#E0007A] bg-[#E0007A]/10 border-[#E0007A]/30",
  Vintage: "text-amber-200 bg-amber-200/10 border-amber-200/25",
  Minimalist: "text-white/80 bg-white/5 border-white/15",
  Street: "text-[#00E5FF] bg-[#00E5FF]/10 border-[#00E5FF]/25",
  Fantasy: "text-violet-300 bg-violet-300/10 border-violet-300/25",
  Cottagecore: "text-amber-100 bg-amber-100/10 border-amber-100/25",
};

function CategoryCard({ prompt }) {
  const [loaded, setLoaded] = useState(false);
  const cat = prompt.style_category;
  const accent = CATEGORY_ACCENTS[cat] || "from-white/25 to-transparent";
  const label = LABEL_STYLES[cat] || "text-white/85 bg-white/5 border-white/15";
  return (
    <Link
      to={`/category/${encodeURIComponent(cat)}`}
      data-testid={`category-card-${cat.toLowerCase().replace(/\s+/g, "-")}`}
      className="group rounded-2xl overflow-hidden bg-[#0E0E12] border border-white/10 hover:border-white/25 transition-colors block"
    >
      <div className="px-3 md:px-4 pt-3 md:pt-4 pb-2 md:pb-3">
        <span
          data-testid={`category-card-label-${cat.toLowerCase().replace(/\s+/g, "-")}`}
          className={`inline-flex items-center text-xs md:text-sm font-extrabold uppercase px-2.5 py-1 rounded-md border ${label}`}
          style={{ letterSpacing: "0.06em" }}
        >
          {cat}
        </span>
      </div>
      <div className="relative aspect-[4/5] bg-[#17171C] overflow-hidden">
        {!loaded && (
          <div className="absolute inset-0 animate-pulse bg-white/[0.02]" />
        )}
        <img
          src={resolveImageUrl(prompt.image_url)}
          alt={`${cat} — ${prompt.title}`}
          width={400}
          height={500}
          loading="lazy"
          decoding="async"
          onLoad={() => setLoaded(true)}
          className={`absolute inset-0 w-full h-full object-cover transition-all duration-500 group-hover:scale-105 ${
            loaded ? "opacity-100" : "opacity-0"
          }`}
        />
        <div className={`absolute inset-0 bg-gradient-to-t ${accent} mix-blend-overlay opacity-70`} />
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#0E0E12] via-[#0E0E12]/60 to-transparent pointer-events-none" />
      </div>

      <div className="p-4 md:p-5">
        <h3 className="font-display text-base md:text-lg tracking-tight leading-tight line-clamp-2 min-h-[2.5rem]">
          {prompt.title}
        </h3>
        <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-mono-label text-[#00E5FF] group-hover:text-white transition-colors">
          View all
          <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
        </div>
      </div>
    </Link>
  );
}

export default function CategoryShowcase() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/prompts/latest-per-category")
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section
      id="categories"
      data-testid="category-showcase"
      className="max-w-[1400px] mx-auto px-5 md:px-10 pt-14 md:pt-20"
    >
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6 md:mb-10">
        <div>
          <div className="font-mono-label text-[#00E5FF]">01 — Browse by style</div>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-2 leading-[1.05]">
            Every category.
            <br className="hidden md:block" />
            <span className="text-white/50"> One tap away.</span>
          </h2>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="aspect-[4/5] rounded-2xl bg-white/[0.03] animate-pulse" />
          ))}
        </div>
      ) : (
        <div
          data-testid="category-grid"
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5"
        >
          {items.map((p) => (
            <CategoryCard key={p.id} prompt={p} />
          ))}
        </div>
      )}
    </section>
  );
}
